import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Eraser } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';

const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

const getLastSelectableMonth = (year) => {
    const today = new Date();
    if (year === today.getFullYear()) return today.getMonth() + 1;
    return year > today.getFullYear() ? 0 : 12;
};

const RefreshStatementDialog = ({ open, onOpenChange, year, onConfirm, loading, progress }) => {
    const [t] = useTranslation('global');
    const [selectedMonths, setSelectedMonths] = useState([]);
    const lastClickedMonthRef = useRef(null);

    const lastSelectableMonth = getLastSelectableMonth(year);
    const selectableMonths = Array.from({ length: lastSelectableMonth }, (_, index) => index + 1);
    const currentMonth = year === new Date().getFullYear() ? new Date().getMonth() + 1 : null;

    const resetSelection = () => {
        setSelectedMonths([]);
        lastClickedMonthRef.current = null;
    };

    const handleOpenChange = (nextOpen) => {
        if (loading) return;
        if (!nextOpen) resetSelection();
        onOpenChange(nextOpen);
    };

    const handleValueChange = (values) => {
        setSelectedMonths(values.map(Number).sort((a, b) => a - b));
    };

    // Shift + clic : selectionne toute la plage depuis le dernier mois clique.
    const handleMonthClick = (event, month) => {
        const lastClickedMonth = lastClickedMonthRef.current;
        lastClickedMonthRef.current = month;

        if (!event.shiftKey || lastClickedMonth === null) return;

        event.preventDefault();
        const [start, end] = [Math.min(lastClickedMonth, month), Math.max(lastClickedMonth, month)];
        const range = selectableMonths.filter((value) => value >= start && value <= end);
        setSelectedMonths((previous) => [...new Set([...previous, ...range])].sort((a, b) => a - b));
    };

    const handleConfirm = async () => {
        if (selectedMonths.length === 0) return;
        await onConfirm(selectedMonths);
        resetSelection();
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        <Label message="consultants.statement.refresh.dialogTitle" size="h4" />
                    </DialogTitle>
                    <DialogDescription className="flex flex-col gap-4 py-4">
                        <Label>{t('consultants.statement.refresh.dialogDescription', { year })}</Label>

                        <div className="flex items-center justify-between gap-firstDegree">
                            <Label fontWeight="semibold">
                                {t('consultants.statement.refresh.selectedCount', { count: selectedMonths.length })}
                            </Label>
                            <div className="flex items-center gap-1">
                                <Button
                                    style="ghost"
                                    sizePadding="small"
                                    showLeftIcon
                                    leftIcon={<CheckCheck className="h-4 w-4" />}
                                    showText={false}
                                    showToolTip
                                    text="consultants.statement.refresh.selectAll"
                                    onClick={() => setSelectedMonths(selectableMonths)}
                                    disabled={loading || selectableMonths.length === 0}
                                />
                                <Button
                                    style="ghost"
                                    sizePadding="small"
                                    showLeftIcon
                                    leftIcon={<Eraser className="h-4 w-4" />}
                                    showText={false}
                                    showToolTip
                                    text="consultants.statement.refresh.clearSelection"
                                    onClick={resetSelection}
                                    disabled={loading || selectedMonths.length === 0}
                                />
                            </div>
                        </div>

                        <ToggleGroup
                            type="multiple"
                            value={selectedMonths.map(String)}
                            onValueChange={handleValueChange}
                            disabled={loading}
                            className="grid grid-cols-4 gap-2 border-0 bg-transparent"
                        >
                            {MONTH_KEYS.map((monthKey, index) => {
                                const month = index + 1;
                                const isSelected = selectedMonths.includes(month);
                                const isDisabled = month > lastSelectableMonth;

                                return (
                                    <ToggleGroupItem
                                        key={monthKey}
                                        value={String(month)}
                                        disabled={isDisabled}
                                        onClick={(event) => handleMonthClick(event, month)}
                                        title={t(`global.months.${monthKey}`)}
                                        className={[
                                            'h-14 flex items-center justify-center rounded-md border border-medium bg-white',
                                            'hover:border-primary hover:bg-hover',
                                            'data-[state=on]:bg-primary data-[state=on]:border-primary data-[state=on]:hover:bg-primary_hover',
                                            month === currentMonth ? 'ring-1 ring-primary ring-offset-1' : '',
                                        ].join(' ')}
                                    >
                                        <Label
                                            fontWeight="semibold"
                                            color={isSelected ? 'white' : (isDisabled ? 'disabled' : 'black')}
                                            message={`global.monthsShort.${monthKey}Short`}
                                        />
                                    </ToggleGroupItem>
                                );
                            })}
                        </ToggleGroup>

                        <Label size="small" color="grey" message="consultants.statement.refresh.rangeHint" />
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="sm:justify-start">
                    <Button
                        type="button"
                        style="ghost"
                        text="global.actions.cancel"
                        onClick={() => handleOpenChange(false)}
                        disabled={loading}
                    />
                    <div className="flex items-center justify-center gap-2">
                        {loading && <span className="loader" />}
                        {loading && progress && (
                            <Label size="small">{t('consultants.statement.refresh.progress', progress)}</Label>
                        )}
                    </div>
                    <Button
                        style="fill"
                        text="consultants.statement.refresh.confirm"
                        className="w-full"
                        onClick={handleConfirm}
                        disabled={selectedMonths.length === 0 || loading}
                    />
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RefreshStatementDialog;
